import { now } from '@/utils/date';

/**
 * Fish-group name suggestions.
 *
 * THIS FILE IS THE ONLY PLACE THE NAMING RULE LIVES. Every "Suggest" button in
 * the feature routes through `suggestFishGroupName`, so changing the convention
 * means editing the constants / helpers below — no call site needs to change.
 *
 * Shape: `FG-[CAGE]-[SEASON][-OPERATION]`
 *
 *   create           ->  FG-C01-26A
 *   split            ->  FG-C02-26A-S
 *   merge            ->  FG-C03-26A-M
 *   split_and_merge  ->  FG-C03-26A-SM
 *   redistribution   ->  FG-C04-26A-R
 *
 * Why each segment:
 *  - `FG`      marks the value as a fish group once the name travels outside
 *              this UI (reports, exports, analytics).
 *  - `[CAGE]`  where the group lives, normalised to `C<number>`. Every result
 *              of one operation lands in a different cage (two destinations in
 *              the same cage is a validation error), so this segment alone
 *              keeps the suggestions of a single operation distinct. A cage
 *              restocked in the same season would repeat a name, so callers
 *              that know the site's names pass them and a clash gets `-2`,
 *              `-3`, ... as stocking names do.
 *  - `[SEASON]` two-digit year + `S` for a spring intake (Jan–Jun) or `A` for
 *              autumn (Jul–Dec): the stocking's when the group joins one,
 *              else taken from today's date.
 *  - operation how the group came to be; this is what separates a split
 *              remainder from the source it stayed in the same cage as.
 */

/** The action that produces the new fish group. */
export type FishGroupNameOperation =
    | 'create'
    | 'split'
    | 'merge'
    | 'split_and_merge'
    | 'redistribution';

const FISH_GROUP_PREFIX = 'FG';
const CAGE_PREFIX = 'C';
const SPRING = 'S';
const AUTUMN = 'A';
/** Zero-based month index of the last spring month (June). */
const LAST_SPRING_MONTH = 5;

const OPERATION_SUFFIX: Record<FishGroupNameOperation, string> = {
    create: '',
    split: 'S',
    merge: 'M',
    split_and_merge: 'SM',
    redistribution: 'R',
};

export interface SuggestFishGroupNameInput {
    /** Cage the new fish group will live in. Blank when not picked yet. */
    cageName: string | null | undefined;
    operation: FishGroupNameOperation;
    /** The season segment of the stocking the group joins, e.g. `27S`. Defaults to today's. */
    season?: string | null;
    /** Fish group names already used at the site, compared ignoring case and padding. */
    existingNames?: readonly string[];
    /** Injectable "today", for tests. Defaults to the current date. */
    reference?: Date;
}

/**
 * Build the suggested name, or `null` when there is not enough information yet
 * (no cage chosen). Callers treat `null` as "nothing to suggest" and disable
 * the button — they never fall back to a partial name.
 */
export function suggestFishGroupName(input: SuggestFishGroupNameInput): string | null {
    const { cageName, operation, season, existingNames = [], reference = now() } = input;

    const cage = cageName?.trim();
    if (!cage) return null;

    const segments = [FISH_GROUP_PREFIX, cageSegment(cage), season || seasonSegment(reference)];

    const suffix = OPERATION_SUFFIX[operation];
    if (suffix) segments.push(suffix);

    return firstFreeName(segments.join('-'), existingNames);
}

function firstFreeName(base: string, existingNames: readonly string[]): string {
    const taken = new Set(existingNames.map((name) => name.trim().toLowerCase()));
    if (!taken.has(base.toLowerCase())) return base;
    for (let counter = 2; ; counter += 1) {
        const candidate = `${base}-${counter}`;
        if (!taken.has(candidate.toLowerCase())) return candidate;
    }
}

/**
 * Cage names are free text and vary per customer (`C01`, `Pen 01`, `M04`), so
 * normalise to `C<number>` off the trailing number — that keeps `Pen 01` and
 * `C01` producing the same segment, and preserves zero padding. Cages with no
 * number in the name fall back to their compacted text rather than inventing
 * one.
 */
function cageSegment(cageName: string): string {
    const trailingNumber = cageName.match(/(\d+)\s*$/);
    if (trailingNumber) return `${CAGE_PREFIX}${trailingNumber[1]}`;
    return `${CAGE_PREFIX}${cageName.replace(/\s+/g, '').toUpperCase()}`;
}

/** Two-digit year plus the intake season, e.g. `26A`. */
function seasonSegment(date: Date): string {
    const year = String(date.getFullYear() % 100).padStart(2, '0');
    const season = date.getMonth() <= LAST_SPRING_MONTH ? SPRING : AUTUMN;
    return `${year}${season}`;
}
